import clsx from "clsx";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import { MdAddBox, MdDelete } from "react-icons/md";
import {
  ETransactionType,
  FILE_STORAGE_SERVER_URL,
  ICON_SIZES,
} from "src/helpers/constants";
import { motion } from "framer-motion";
import { memo, useId, useMemo, useState } from "react";
import useAllTransactions from "src/hooks/useAllTransactions";
import Icon from "../Icon/Icon";
import { useNavigate } from "react-router-dom";
import useCurrentUser from "src/hooks/useCurrentUser";
import {
  formatCurrency,
  formatLongDate,
  getCurrencyDataByCode,
  getShortName,
  showNotification,
  transactionDescription,
} from "src/helpers/utils";
import useMobile from "src/hooks/useMobile";
import { Tooltip } from "react-tooltip";
import useUsers from "src/hooks/useUsers";
import moment from "moment";
import NetworkProfile from "../NetworkProfile/NetworkProfile";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import ImportReceivedTransaction from "../ImportReceivedTransaction/ImportReceivedTransaction";
import useCache from "src/hooks/useCache";
import { deleteTransactions, updateTransaction } from "src/api/transactions";
import { EButtonKind } from "../Button/ButtonTypes";
import { useTranslation } from "react-i18next";

const MOBILE_SIZE = 58;
const DESKTOP_SIZE = 64;

interface IReceivedTransactionCard {
  transaction: any;
  index: number;
}

const ReceivedTransactionCard: React.FC<IReceivedTransactionCard> = ({
  transaction,
  index,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const { clearCache } = useCache();
  const [isImportingTransaction, setIsImportingTransaction] =
    useState<boolean>(false);
  const { getUserById } = useUsers();
  const { getSourceCustomSettings } = useAllTransactions();
  const sourceTooltipId = useId();
  const [showUserInfoModal, setShowUserInfoModal] = useState<boolean>(false);
  const [showImportTransactionDialog, setShowImportTransactionDialog] =
    useState<boolean>(false);
  const [isDeletingTransaction, setIsDeletingTransaction] =
    useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [activeTransaction, setActiveTransaction] = useState<any>(null);

  const navigate = useNavigate();
  const { getAccountById } = useCurrentUser();

  const sharedByInfo = useMemo(() => {
    return transaction?.sharedBy;
  }, [transaction]);

  const currencySymbol = useMemo(() => {
    return (
      getCurrencyDataByCode({ code: sharedByInfo?.currencyCode })?.symbol || ""
    );
  }, [sharedByInfo]);

  const description = useMemo(() => {
    return transactionDescription(transaction);
  }, [transaction]);

  const userInfo = useMemo(() => {
    return getUserById({ _id: sharedByInfo?.user });
  }, [sharedByInfo]);

  const deleteTransaction = async () => {
    let hasError = false;

    setIsDeletingTransaction(true);

    await deleteTransactions({
      transactionIds: [activeTransaction?.invoice],
      sendToRecycle: false,
    }).then(async (data) => {
      if (hasError) {
        showNotification({
          kind: "error",
          message: `Error eliminando la transacción compartida`,
        });
        setIsDeletingTransaction(false);
      } else {
        await clearCache();

        showNotification({
          message: "Transacción eliminada permanentemente",
          kind: "success",
        });
      }

      setIsDeletingTransaction(false);
      setShowDeleteDialog(false);
    });
  };

  const updateTransactionData = async ({
    account,
    type,
    payTo,
    date,
  }: {
    account: string;
    type: ETransactionType;
    payTo: string;
    date: string;
  }) => {
    setIsImportingTransaction(true);
    try {
      const result = await updateTransaction({
        transactionId: activeTransaction?.invoice || "",
        fieldsToUpdate: {
          account,
          shared: false,
          payTo,
          type,
          date: moment(date).format("yyyy-MM-DD"),
        },
      });

      if (result?.success) {
        await clearCache();

        setShowImportTransactionDialog(false);
        setIsImportingTransaction(false);

        showNotification({
          message: "Transacción importada correctamente",
          kind: "success",
        });
      } else {
        setIsImportingTransaction(false);
        showNotification({
          message: "Ocurrió un error al importar la transacción",
          kind: "error",
        });
      }
    } catch (e) {
      setIsImportingTransaction(false);
      showNotification({
        message: "Ocurrió un error al importar la transacción",
        kind: "error",
      });
    }
  };

  return (
    <>
      <motion.div
        className="flex w-full"
        initial={{
          opacity: 0,
          x: index % 2 === 0 ? -100 : 100,
        }}
        animate={{ opacity: 1, x: 0 }}
        transition={{
          duration: 0.2,
          ease: "easeOut",
          delay: index * 0.1,
        }}
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "bg-sky-200/60 dark:bg-gray-900/50",
            "p-3 md:p-3",
            "rounded-md",
            "md:gap-4",
            "gap-2",
          ])}
        >
          <div className={clsx(["flex", "flex-row", "gap-2"])}>
            <div
              className={clsx([
                `!min-w-[${MOBILE_SIZE}px]`,
                `!min-h-[${MOBILE_SIZE}px]`,
                `!max-w-[${MOBILE_SIZE}px]`,
                `!max-h-[${MOBILE_SIZE}px]`,
                `md:!min-w-[${DESKTOP_SIZE}px]`,
                `md:!min-h-[${DESKTOP_SIZE}px]`,
                `md:!max-w-[${DESKTOP_SIZE}px]`,
                `md:!max-h-[${DESKTOP_SIZE}px]`,
                "rounded-md",
                "overflow-hidden",
                "inline-flex",
                "items-center",
                "justify-center",
                "cursor-pointer",
                "relative",
              ])}
              data-tooltip-id={sourceTooltipId}
              data-tooltip-content={isMobile ? "" : "Ver perfil"}
              data-tooltip-position-strategy="fixed"
              data-tooltip-open-on="hover"
              data-tooltip-offset={0}
              onClick={(e: any) => {
                e?.preventDefault?.();
                setShowUserInfoModal(true);
              }}
            >
              {userInfo?.picture ? (
                <img
                  src={`${FILE_STORAGE_SERVER_URL}/profile-thumbnail-${userInfo?.picture}`}
                  className={clsx(["bg-checkers", "!w-full", "!h-full"])}
                  alt={transaction?.payTo}
                />
              ) : (
                <Icon
                  name={userInfo?.icon}
                  size={isMobile ? ICON_SIZES.MD : ICON_SIZES.MD_LG}
                />
              )}
              {!isMobile && (
                <Tooltip
                  id={sourceTooltipId}
                  style={{ zIndex: 99 }}
                  positionStrategy="fixed"
                />
              )}
            </div>
            <div className={clsx(["flex", "flex-col", "w-full"])}>
              <div
                className={clsx([
                  "text-sm",
                  "md:text-base",
                  "font-normal",
                  "truncate",
                ])}
              >
                {description}
              </div>
              <div className={clsx(["text-sm", "truncate"])}>
                {transaction?.payTo}
              </div>
              <div className={clsx(["text-sm", "font-medium", "truncate"])}>
                {formatLongDate(transaction?.date)}
              </div>
              <div className="text-xs">
                Enviada por {userInfo?.name} el{" "}
                {formatLongDate(sharedByInfo?.date)}
              </div>
            </div>
          </div>
          <div
            className={clsx([
              "flex",
              "w-full",
              "flex-row",
              "gap-6",
              "justify-between",
              "items-center",
            ])}
          >
            <div
              className={clsx([
                "flex",
                "flex-row",
                "items-center",
                "text-xl",
                "md:text-2xl",
                "font-medium",
                "truncate",
                "dark:text-purple-400",
                "text-blue-400",
              ])}
            >
              {formatCurrency({
                value: transaction?.total,
                currency: currencySymbol,
              })}
            </div>
            <ButtonGroup
              buttons={[
                {
                  tooltip: t("common_tooltips.delete"),
                  icon: <MdDelete size={ICON_SIZES.MD} />,
                  onClick: (e: any) => {
                    e?.preventDefault?.();
                    setActiveTransaction(transaction);
                    setShowDeleteDialog(true);
                  },
                  kind: EButtonKind.DANGER,
                },
                {
                  tooltip: t("common_tooltips.import"),
                  icon: <MdAddBox size={ICON_SIZES.MD} />,
                  onClick: (e: any) => {
                    e?.preventDefault?.();
                    setActiveTransaction(transaction);
                    setShowImportTransactionDialog(true);
                  },
                  kind: EButtonKind.SAVE,
                },
              ]}
              align="right"
            />
          </div>
        </div>
      </motion.div>

      {showUserInfoModal && (
        <NetworkProfile
          profileInfo={userInfo}
          title={
            userInfo?.name
              ? `Acerca de ${getShortName(userInfo?.name)}`
              : "Perfil"
          }
          onClose={() => setShowUserInfoModal(false)}
          actions={[]}
        />
      )}

      {showImportTransactionDialog && (
        <ImportReceivedTransaction
          onClose={() => setShowImportTransactionDialog(false)}
          onConfirm={async ({ account, type, payTo, date }) => {
            await updateTransactionData({ account, type, payTo, date });
          }}
          transaction={activeTransaction}
          isLoading={isImportingTransaction}
        />
      )}

      {showDeleteDialog && (
        <ConfirmDialog
          description="¿Deseas eliminar esta transacción compartida de forma permanente?"
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={async () => {
            await deleteTransaction();
          }}
          title={t("common_dialog_titles.delete")}
          confirmKind={EButtonKind.DANGER}
          doubleConfirmRequired
          isLoading={isDeletingTransaction}
        />
      )}
    </>
  );
};

export default memo(ReceivedTransactionCard);
